/*************************************************************************
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying
 * it. If you have received this file from a source other than Adobe,
 * then your use, modification, or distribution of it requires the prior
 * written permission of Adobe.
 **************************************************************************/

/**
 * Internal APIs, meant to be used by Unified Shell only.
 * @packageDocumentation
 * @module ai
 */
import EventEmitter from './src/EventEmitter';
import {getImpl} from './src/Global';
import type {JSX} from 'react';

export interface Prompt {
  icon: JSX.Element;
  label: string;
  name: string;
}

export interface Source {
  citationNumber?: number;
  commentedQuery?: string;
  commented_query?: string;
  endIndex: number;
  startIndex: number;
  title?: string;
  url: string;
}

export interface SuccessResponse {
  answer: string;
  chatId: string;
  interactionId: string;
  prompts: Prompt[];
  date: Date | null;
  sources: Source[];
  promptSuggestions: string[];
  clarifications?: {message?: string, questions: string[]};
  reportingDataDefinition?: any;
  usageInsights: string;
  visualizations: any[];
}

export interface UserActionResponse {
  action: string;
  data?: any;
}

export interface SystemResponse {
  message: string;
}

export interface ErrorResponse {
  error: boolean;
  date: Date | null;
  message?: string;
  requestId?: string;
  status: number;
}

export type Response = SuccessResponse | UserActionResponse | SystemResponse | ErrorResponse;

export enum AIMessageType {
  CLOSE = 'CLOSE',
  CONTEXT = 'CONTEXT',
  ENABLE_ASSISTANT = 'ENABLE_ASSISTANT',
  ERROR = 'ERROR',
  OPEN = 'OPEN',
  QUERY = 'QUERY',
  RESPONSE = 'RESPONSE',
  USER_ACTION = 'USER_ACTION'
}

export interface AIMessagePayload {
  context?: AIContext;
  /**
   * The permissions required to enable the AI UI.
   */
  permissions?: string[];
  /**
   * The query that was sent to the AI.
   */
  query?: string;
  /**
   * The response from the AI.
   */
  response?: Response;
  /**
   * The type of message.
   */
  type: AIMessageType;
}

export interface AIContext {
  /**
   * Catch all for now.
   */
  [key: string]: any;
}

interface AIMessageEvent {
  CONTEXT: AIMessagePayload;
  ENABLE_ASSISTANT: AIMessagePayload;
  ERROR: AIMessagePayload;
  OPEN: AIMessagePayload;
  QUERY: AIMessagePayload;
  RESPONSE: AIMessagePayload;
}

export interface AIApi extends EventEmitter<AIMessageEvent> {
  /**
   * Sets the context for the AI chat. This can be called unlimited times to
   * ensure the context has the correct information. This value will be fetched
   * on every request for context by the AI chat UI when a user submits a query.
   * Please make sure to keep this value up-to-date when any context values
   * change within the application.
   *
   * ***Example:***
   *
   * ```typescript
   * ai.context = {...};
   * ```
   */
  context?: AIContext;

  /**
   * Closes the chat side rail.
   *
   * ***Example:***
   *
   * ```typescript
   * ai.closeChat();
   * ```
   */
  closeChat: () => void;

  /**
   * Requests the context from the application. This is used to asynchronously
   * request the context on-demand.
   *
   * ***Example:***
   *
   * ```typescript
   * ai.onContextRequest(async () => ({...}));
   * ```
   */
  onContextRequest: (callback: () => Promise<AIContext>) => void;

  /**
   * Opens the chat side rail with an optional pre-populated query.
   *
   * ***Example:***
   *
   * ```typescript
   * ai.openChat('How do I create a new project?');
   * ```
   */
  openChat: (query?: string) => void;

  /**
   * Send a message to the AI chat.
   *
   * ***Example:***
   *
   * ```typescript
   * ai.sendMessage({type: AIMessageType.ACTION_COMPLETE});
   * ```
   */
  sendMessage: (payload: AIMessagePayload) => void;

  /**
   * Send a message to Unified Shell to show the Assistant. This includes
   * permissions that Unified Shell will validate to determine for access.
   *
   * ***Example:***
   *
   * ```typescript
   * ai.showAssistant(['permission1', 'permission2']);
   * ```
   */
  showAssistant: (permissions: string[]) => void;
}

const ai = {
  closeChat: () => {
    return getImpl('ai')().closeChat();
  },
  emit: (type, evt) => {
    return getImpl('ai')().emit(type, evt);
  },
  off: (type, handler) => {
    return getImpl('ai')().off(type, handler);
  },
  on: (type, handler) => {
    return getImpl('ai')().on(type, handler);
  },
  onContextRequest: (callback: () => Promise<AIContext>) => {
    return getImpl('ai')().onContextRequest(callback);
  },
  openChat: (query?: string) => {
    return getImpl('ai')().openChat(query);
  },
  sendMessage: (payload: AIMessagePayload) => {
    return getImpl('ai')().sendMessage(payload);
  },
  showAssistant: (permissions: string[]) => {
    return getImpl('ai')().showAssistant(permissions);
  }
} as AIApi;

Object.defineProperty(ai, 'context', {
  get: (): AIContext | undefined => {
    return getImpl('ai')().context;
  },
  set: (value: AIContext | undefined) => {
    getImpl('ai')().context = value;
  }
});

export default ai;
